import { useEffect, useState } from 'react';
import api from '../api/axios';
import styles from './Table.module.css';

export default function Settings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get('/settings')
      .then((r) => setForm(r.data))
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMsg('');
    try {
      const { data } = await api.put('/settings', {
        maxAdsPerUser: Number(form.maxAdsPerUser),
        adExpiryDays: Number(form.adExpiryDays),
        maxImagesPerAd: Number(form.maxImagesPerAd),
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        maintenanceMode: !!form.maintenanceMode,
        maintenanceMessage: form.maintenanceMessage,
        featuredAdsLimit: Number(form.featuredAdsLimit),
      });
      setForm(data);
      setMsg('Settings saved');
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.center}>Loading...</div>;
  if (!form) return <div className={styles.center}>{error || 'No settings'}</div>;

  return (
    <div>
      <div className={styles.header}>
        <h2 className={styles.title}>App Settings</h2>
      </div>
      {error && <div className={styles.formError}>{error}</div>}
      {msg && (
        <div style={{ background: '#e8f8ef', color: '#1a9e5c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontWeight: 600 }}>
          {msg}
        </div>
      )}
      <form
        onSubmit={save}
        className={styles.form}
        style={{
          maxWidth: 560,
          background: '#fff',
          padding: 24,
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#1a1a2e' }}>
          Posting limits
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: '#888' }}>
          These apply to all users when posting or editing ads.
        </p>

        <label>Total ads a user can add (0 = unlimited)</label>
        <input
          type="number"
          min={0}
          max={500}
          value={form.maxAdsPerUser ?? 20}
          onChange={(e) => setForm({ ...form, maxAdsPerUser: e.target.value })}
          required
        />

        <label>Days until an ad expires</label>
        <input
          type="number"
          min={1}
          max={365}
          value={form.adExpiryDays}
          onChange={(e) => setForm({ ...form, adExpiryDays: e.target.value })}
          required
        />

        <label>Max images per ad</label>
        <input
          type="number"
          min={1}
          max={10}
          value={form.maxImagesPerAd}
          onChange={(e) => setForm({ ...form, maxImagesPerAd: e.target.value })}
          required
        />

        <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '8px 0' }} />

        <label>Featured ads limit (home)</label>
        <input
          type="number"
          min={1}
          max={100}
          value={form.featuredAdsLimit}
          onChange={(e) => setForm({ ...form, featuredAdsLimit: e.target.value })}
          required
        />
        <label>Contact email</label>
        <input
          type="email"
          value={form.contactEmail || ''}
          onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
        />
        <label>Contact phone</label>
        <input
          value={form.contactPhone || ''}
          onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
        />
        <label className={styles.checkLabel}>
          <input
            type="checkbox"
            checked={!!form.maintenanceMode}
            onChange={(e) => setForm({ ...form, maintenanceMode: e.target.checked })}
          />
          Maintenance mode
        </label>
        <label>Maintenance message</label>
        <textarea
          rows={3}
          value={form.maintenanceMessage || ''}
          onChange={(e) => setForm({ ...form, maintenanceMessage: e.target.value })}
        />
        <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
          Push notifications need <code>FCM_SERVER_KEY</code> in server <code>.env</code>.
        </p>
        <div className={styles.modalActions}>
          <button type="submit" className={styles.saveBtn} disabled={saving}>
            {saving ? 'Saving...' : 'Save settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
